import React from 'react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import miniloader from '../assets/img/svg/miniloader.svg'
import sprite from '../assets/img/svg/svg-sprite.svg'
import { useCopyToClipboard } from '../hooks/useCopyToClipboard'
import { isRobotYandexDeliveryType } from '../robotDelivery'
import { isSourceHandoverExchanging } from '../robotHandover'
import {
  useGetOrderQuery,
  useUpdateCornerStatusMutation,
  useUpdateOrderStatusMutation,
  useCheckYandexCodeMutation,
  useRobotOpenLidMutation,
  useRobotCloseLidMutation,
} from '../store/api/api'
import type { CornerStatus, Order } from '../store/types'
import { parseEatPlace, translateToStyleCorner } from '../utils'

const getTime = (time: number) => {
  const date = new Date(time * 1000)

  const hours = date.getHours()
  const minutes = `0${date.getMinutes()}`

  return `${hours}:${minutes.substr(-2)}`
}

const totalQty = (order: Order) =>
  order.order_outlets.reduce((totalQty, outlet) => outlet.order_items.length + totalQty, 0)

const safeImgSrc = (src: string | null | undefined): string | null => {
  if (src == null) return null
  const t = String(src).trim()
  if (!t || t === 'null' || t === 'undefined') return null
  return t
}

const Order = () => {
  // --- EXTERNAL STATE --- //

  const navigate = useNavigate()

  const { orderId } = useParams<{ orderId?: string }>()

  const { copyToClipboard } = useCopyToClipboard()

  const [isCopying, setIsCopying] = useState(false)

  const numericOrderId = orderId ? parseInt(orderId) : null

  const { data: order } = useGetOrderQuery(numericOrderId!, {
    skip: numericOrderId == null,
  })

  const [updateCornerStatus] = useUpdateCornerStatusMutation()
  const [updateOrderStatus] = useUpdateOrderStatusMutation()
  const [robotOpenLid, { isLoading: robotOpenLidLoading }] = useRobotOpenLidMutation()
  const [robotCloseLid, { isLoading: robotCloseLidLoading }] = useRobotCloseLidMutation()
  const [checkYandexCode, { isLoading: checkYandexCodeLoading }] = useCheckYandexCodeMutation()

  const [isSberCodesOpen, setIsSberCodesOpen] = useState(false)
  const [isYandexCodeOpen, setIsYandexCodeOpen] = useState(false)
  const [yandexCode, setYandexCode] = useState('')
  const [yandexCodeResult, setYandexCodeResult] = useState<'valid' | 'invalid' | string | null>(null)

  const robotLoading = robotOpenLidLoading || robotCloseLidLoading

  console.log(order?.full_cost)
  console.log(order?.delivery_cost)

  // --- CONSTATNTS --- //

  const listIssuedCourier = order ? order.order_outlets.filter((outlet) => outlet.status === 'issued_courier') : []

  const ext = order?.externalDelivery
  const robotExchanging = isSourceHandoverExchanging(ext)
  const robotReadyAtMarket = ext?.readyForPickup === true
  const robotEnRouteToCustomer = ext?.pickupCompleted === true || ext?.pickupVisitStatus === 'visited'
  const robotLidActionEnabled = robotExchanging || robotReadyAtMarket
  const robotOpenLidDisabled = robotLoading || !robotLidActionEnabled
  const robotButtonLabel = robotExchanging
    ? 'Закрыть крышку'
    : robotReadyAtMarket
    ? 'Открыть крышку'
    : robotEnRouteToCustomer
    ? 'Едет к клиенту'
    : 'Едет к рынку'

  const onRobotLidClick = async () => {
    if (!order) return
    if (robotExchanging) {
      await robotCloseLid({ orderId: order.id })
    } else {
      await robotOpenLid({ orderId: order.id })
    }
  }

  const hasSberCodes = Boolean(order?.sberCourierHandoutCode || order?.sberCourierReturnCode)

  // --- CALLBACKS --- //

  const closeYandexCodePopup = () => {
    setIsYandexCodeOpen(false)
    setYandexCode('')
    setYandexCodeResult(null)
  }

  const handleCheckYandexCode = async () => {
    if (!order || !yandexCode.trim()) return

    setYandexCodeResult(null)

    try {
      const result = await checkYandexCode({ orderId: order.id, code: yandexCode.trim() }).unwrap()
      setYandexCodeResult(result.isValid ? 'valid' : 'invalid')
    } catch (e: any) {
      setYandexCodeResult(e?.data?.message || 'Не удалось проверить код')
    }
  }

  const goBack = () => {
    navigate('/market')
  }

  const handleUpdateCornerStatus = (outletOrderId: number, status: CornerStatus) => async () => {
    await updateCornerStatus({ orderId: numericOrderId!, outletOrderId, status })
  }

  const updateOrderStatusHandler = React.useCallback(async () => {
    if (!order) return
    if (listIssuedCourier.length === 0) return

    const listIssuedCourierId = listIssuedCourier.map((outlet) => outlet.id)

    // Update all issued courier outlets to received (parallel)
    const results = await Promise.allSettled(
      listIssuedCourierId.map((outletOrderId) =>
        updateCornerStatus({ orderId: order.id, outletOrderId, status: 'received' }).unwrap(),
      ),
    )

    if (results.some((r) => r.status === 'rejected')) {
      return
    }

    const unproceedOutlets = order.order_outlets.filter(
      (outlet) => outlet.status !== 'received' && !listIssuedCourierId.includes(outlet.id),
    )

    if (unproceedOutlets.length > 0) return

    await updateOrderStatus({ orderId: order.id })
  }, [order, listIssuedCourier, updateCornerStatus, updateOrderStatus])

  // --- RENDERING --- //

  console.log(order)

  return order ? (
    <div className="page__container">
      <div className="page__content">
        <div className="order">
          <div className="order__title">
            <svg width="12" height="14" onClick={goBack}>
              <use href={sprite + '#icon-back'} />
            </svg>
            <span>{`ЗАКАЗ №${order?.id} (${getTime(order.created_at)})`}</span>
            <div className="order__logo">
              <svg width="24" height="24"></svg>
            </div>
          </div>
          <div className="order__table">
            <div className="order__row">
              <div className="order__col order__col-name">Клиент</div>
              <div className="order__col order__col-value order__client">
                {order.name ? <span>{order.name}</span> : null}
                {order.phone ? <a href={`tel:${order.phone.replace(/\D/g, '')}`}>{order.phone}</a> : null}
              </div>
            </div>
            <div className="order__row">
              <div className="order__col order__col-name">Где будет есть</div>
              <div className="order__col order__col-value">
                {parseEatPlace(order.eat_place, order.car_number, order.table)}
              </div>
            </div>
            {(() => {
              const dt = order.yandexDeliveryType
              const showFasterTag =
                (dt == null || dt === '') &&
                (order.eat_place === 'togo_yandex' ||
                  order.eat_place === 'togo_delivery' ||
                  order.eat_place === 'togo_courier')
              const showDeliveryRow = isRobotYandexDeliveryType(dt) || dt === 'express' || showFasterTag
              if (!showDeliveryRow) return null
              return (
                <div className="order__row">
                  <div className="order__col order__col-name">Доставка</div>
                  <div className="order__col order__col-value order__col-value--tag">
                    {isRobotYandexDeliveryType(dt) ? (
                      <span className="runner-rover-tag runner-rover-tag--inline" title="Доставка роботом Яндекса">
                        {dt === 'robot_d2d' ? 'Ровер до двери' : 'Ровер'}
                      </span>
                    ) : dt === 'express' ? (
                      <span className="runner-rover-tag runner-rover-tag--inline" title="Яндекс Курьер">
                        Яндекс Курьер
                      </span>
                    ) : (
                      <span
                        className="runner-rover-tag runner-rover-tag--inline runner-rover-tag--faster"
                        title="Доставка Faster"
                      >
                        Faster
                      </span>
                    )}
                  </div>
                </div>
              )
            })()}
            {ext?.courierName ? (
              <div className="order__row">
                <div className="order__col order__col-name">Курьер</div>
                <div className="order__col order__col-value">{ext.courierName}</div>
              </div>
            ) : null}
            {order.eat_place === 'togo_delivery' ? (
              <div className="order__row">
                <div className="order__col order__col-name">Адрес</div>
                <div className="order__col order__col-value">
                  {order.delivery_street}, д.{order.delivery_building}, кв.{order.delivery_apartment}
                </div>
              </div>
            ) : null}
            {order.eat_place === 'togo_yandex' || order.eat_place === 'togo_yandex_pickup' ? (
              <>
                <div className="order__row">
                  <div className="order__col order__col-name">Номер Яндекса</div>
                  <div className="order__col order__col-value">{order.eats_id}</div>
                </div>
                <div className="order__row">
                  <div className="order__col order__col-name">Код курьера</div>
                  <div className="order__col order__col-value">
                    <button
                      className="order__code-btn"
                      type="button"
                      onClick={() => setIsYandexCodeOpen(true)}
                    >
                      Проверить код
                    </button>
                  </div>
                </div>
              </>
            ) : null}
            {order.eat_place === 'togo_sber' ? (
              <div className="order__row">
                <div className="order__col order__col-name">Номер Купера</div>
                <div className="order__col order__col-value">{order.eats_id}</div>
              </div>
            ) : null}
            {order.eat_place === 'consumer' ? (
              <div className="order__row">
                <div className="order__col order__col-name">Номер Купера</div>
                <div className="order__col order__col-value">{order.eats_id}</div>
              </div>
            ) : null}
            {hasSberCodes ? (
              <div className="order__row">
                <div className="order__col order__col-name">Коды</div>
                <div className="order__col order__col-value">
                  <button className="order__code-btn" type="button" onClick={() => setIsSberCodesOpen(true)}>
                    Посмотреть
                  </button>
                </div>
              </div>
            ) : null}
            {order.eat_place === 'togo_vkusvill' ? (
              <div className="order__row">
                <div className="order__col order__col-name">Номер ВкусВилл</div>
                <div className="order__col order__col-value">{order.eats_id}</div>
              </div>
            ) : null}
            {order.discriminator === 'marketplace' ? (
              <div className="order__row">
                <div className="order__col order__col-name">Адрес доставки</div>
                <div className="order__col order__col-value">
                  <span
                    onClick={(e) => {
                      e.stopPropagation()
                      const address = order.address || ''
                      if (!address) return

                      setIsCopying(true)
                      const el = e.currentTarget
                      el.style.backgroundColor = '#e0f7fa'

                      copyToClipboard(address, 'Адрес скопирован!')

                      setTimeout(() => {
                        el.style.backgroundColor = ''
                        setIsCopying(false)
                      }, 1500)
                    }}
                    style={{
                      cursor: 'pointer',
                      padding: '2px 4px',
                      borderRadius: '3px',
                      transition: 'background-color 0.2s ease',
                      opacity: isCopying ? 0.9 : 1,
                    }}
                    title="Нажмите, чтобы скопировать адрес"
                  >
                    {order.address}📋
                  </span>
                </div>
              </div>
            ) : null}
            {order.eat_place === 'car' ? (
              <div className="order__row">
                <div className="order__col order__col-name">К выходу</div>
                <div className="order__col order__col-value">{order.market_street_title}</div>
              </div>
            ) : null}
            <div className="order__row">
              <div className="order__col order__col-name">Количество приборов</div>
              <div className="order__col order__col-value">{order.cutlery}</div>
            </div>
            <div className="order__row">
              <div className="order__col order__col-name">Приготовить</div>
              <div className="order__col order__col-value">
                {order.ready_quickly === 1 ? 'как можно скорее' : `к ${getTime(Number(order.ready_time))}`}
              </div>
            </div>
            <div className="order__row">
              <div className="order__col order__col-name">Комментарий</div>
              <div className="order__col order__col-value">{order.note}</div>
            </div>
          </div>
          <div className="order__total">
            <div className="order__subtotal1">
              <div className="order__total-text">ИТОГО</div>
              <div className="order__total-sum">
                {totalQty(order)} поз. ({(order.full_cost - order.delivery_cost).toFixed(2)} ₽)
              </div>
            </div>
            {order.delivery_cost > 0 ? (
              <div className="order__subtotal2">
                <div className="order__col order__col-value"> и доставка {order.delivery_cost} ₽</div>
              </div>
            ) : null}
          </div>

          <div className="order__items">
            {order.order_outlets.map(({ id, status, outlet, order_items, order_items_full_sum }) => (
              <div className="order__item order__item_active" key={id}>
                <div className="order__header">
                  <div className="order__name">
                    <div className={`order__header-img${!safeImgSrc(outlet.image) ? ' order__header-img--empty' : ''}`}>
                      {safeImgSrc(outlet.image) ? <img src={safeImgSrc(outlet.image)!} alt="" /> : null}
                    </div>
                    <div className="order__header-name">{outlet.title}</div>
                  </div>
                  {!['market_corner', 'togo_corner'].includes(order.eat_place) && status === 'ready' ? (
                    <button
                      className="order__header-btn order__status_readybtn"
                      onClick={handleUpdateCornerStatus(id, 'issued_courier')}
                    >
                      <span>Забрать</span>
                    </button>
                  ) : (
                    <div className={`order__header-btn order__status_${status}`}>
                      {translateToStyleCorner(status) || status}
                    </div>
                  )}
                </div>
                <div className="order__body">
                  {order_items.map((item) => (
                    <div className="order__body-item" key={item.id}>
                      <div className="order__body-row">
                        {safeImgSrc(item?.image) ? (
                          <img className="card__img" src={safeImgSrc(item.image)!} width="30px" height="30px" alt="" />
                        ) : (
                          <div className="card__img card__img--placeholder" aria-hidden />
                        )}
                        <div className="order__position">
                          <div className="order__dish">{item.title}</div>
                          <div className="order__weight">
                            {Number(item.measure)} {item.units === 'g' ? 'гр' : item.units === 'ml' ? 'мл' : ' '}
                          </div>
                        </div>
                        <div className="order__pricesum">
                          <div className="order__number">{item.qty_item}</div>
                          <div className="order__itemprice">{item.price_item / (1 - item.discount_percent)}₽</div>
                          <div className="order__sum order__sum_md">
                            {Math.round(item.qty_item * (item.price_item / (1 - item.discount_percent)) * 10) / 10}₽
                          </div>
                        </div>
                      </div>
                      {item.note ? (
                        <div className="order__itemnote">
                          <span className="note__title">От клиента: </span>
                          {item.note}
                        </div>
                      ) : null}

                      {item.options.map((option) => (
                        <div className="order__body-row" key={option.id}>
                          <div className="order__num"></div>
                          <div className="order__dish-extra">+ {option.title}</div>
                          <div className="order__extra-pricesum">
                            <div className="order__number-extra">{item.qty_item}</div>
                            <div className="order__sum-extra">{option.price} ₽</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                  <div className="order__footer">
                    <div className="order__body-row">
                      <div className="order__num"></div>
                      <div className="order__dish"></div>
                      <div className="order__pricesum">
                        <div className="order__number">
                          ИТОГО:
                        </div>
                        <div className="order__sum">{Math.round(order_items_full_sum * 10) / 10} ₽</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {listIssuedCourier.length > 0 ? (
            <div className="order__button-wrap">
              {isRobotYandexDeliveryType(order.yandexDeliveryType) ? (
                <button
                  className="order__button order__button_accept"
                  type="button"
                  disabled={robotOpenLidDisabled}
                  onClick={onRobotLidClick}
                >
                  {robotLoading ? <img src={miniloader} width="22px" height="22px" alt="" /> : null}
                  {robotButtonLabel}
                </button>
              ) : (
                <button
                  className="order__button order__button_accept"
                  type="button"
                  disabled={listIssuedCourier.length === 0}
                  onClick={updateOrderStatusHandler}
                >
                  <svg width="23" height="17">
                    <use href={sprite + '#icon-check'} />
                  </svg>
                  Отдать заказ клиенту
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {isSberCodesOpen ? (
        <div className="order-popup" onClick={() => setIsSberCodesOpen(false)}>
          <div className="order-popup__body" onClick={(e) => e.stopPropagation()}>
            <div className="order-popup__title">Коды Купера</div>
            <div className="order-popup__code">
              Код выдачи курьеру: <b>{order.sberCourierHandoutCode || '—'}</b>
            </div>
            <div className="order-popup__code">
              Код возврата: <b>{order.sberCourierReturnCode || '—'}</b>
            </div>
            <button className="order-popup__btn" type="button" onClick={() => setIsSberCodesOpen(false)}>
              Закрыть
            </button>
          </div>
        </div>
      ) : null}

      {isYandexCodeOpen ? (
        <div className="order-popup" onClick={closeYandexCodePopup}>
          <div className="order-popup__body" onClick={(e) => e.stopPropagation()}>
            <div className="order-popup__title">Код курьера Яндекса</div>
            <input
              className="order-popup__input"
              type="text"
              inputMode="numeric"
              autoFocus
              placeholder="Код, который назвал курьер"
              value={yandexCode}
              onChange={(e) => {
                setYandexCode(e.target.value)
                setYandexCodeResult(null)
              }}
            />
            {yandexCodeResult === 'valid' ? (
              <div className="order-popup__result order-popup__result--ok">Код верный</div>
            ) : yandexCodeResult === 'invalid' ? (
              <div className="order-popup__result order-popup__result--fail">Код неверный</div>
            ) : yandexCodeResult ? (
              <div className="order-popup__result order-popup__result--fail">{yandexCodeResult}</div>
            ) : null}
            <button
              className="order-popup__btn order-popup__btn--accent"
              type="button"
              disabled={checkYandexCodeLoading || !yandexCode.trim()}
              onClick={handleCheckYandexCode}
            >
              {checkYandexCodeLoading ? <img src={miniloader} width="22px" height="22px" alt="" /> : null}
              Проверить
            </button>
            <button className="order-popup__btn" type="button" onClick={closeYandexCodePopup}>
              Закрыть
            </button>
          </div>
        </div>
      ) : null}
    </div>
  ) : null
}

export default Order
